import { Box, Checkbox, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';
import {
  Column,
  Row,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { ZteEnrolmentStatus } from '~graphql/__generated__/types';
import { ZteTableFragment } from './__generated__/ZteTable.graphql';
import { Columns } from './types';

const TableCell = generateTableCellComponent<ZteTableFragment, ReactNode>();

const DateCell = ({ value }: { value: string }) => (
  <Box whiteSpace="nowrap">{value ? format(new Date(value), 'MMM d, y') : 'Never'}</Box>
);

const statusCell = ({ value }: { value: string }) => (
  <Text fontWeight="medium">{value === ZteEnrolmentStatus.Claimed ? 'Yes' : 'No'}</Text>
);
const TableCellBold = generateTableCellComponent<ZteTableFragment, ReactNode>({
  isBold: true,
});

export function useZteTable({
  data,
  pageIndex,
  pageSize,
  totalRowCount,
}: {
  data: ZteTableFragment[];
  pageIndex: number;
  pageSize: number;
  totalRowCount: number;
}) {
  const columns = useMemo<Array<Column<ZteTableFragment>>>(
    () => [
      {
        id: Columns.Selection,
        Header: ({ getToggleAllPageRowsSelectedProps }) => {
          const { checked, indeterminate, onChange, role, title } =
            getToggleAllPageRowsSelectedProps();

          return (
            <Checkbox
              isChecked={checked}
              isIndeterminate={indeterminate}
              onChange={onChange}
              role={role}
              title={title}
            />
          );
        },
        Cell: ({ row }: { row: Row<ZteTableFragment> }) => {
          const { checked, indeterminate, onChange, role, title } = row.getToggleRowSelectedProps();

          return (
            <StopClickPropagation>
              <Checkbox
                isChecked={checked}
                isIndeterminate={indeterminate}
                onChange={onChange}
                role={role}
                title={title}
              />
            </StopClickPropagation>
          );
        },
        width: '50px',
        maxWidth: 50,
        minWidth: 20,
      },
      {
        id: Columns.Batchname,
        Header: t('batchId'),
        accessor: (zte) => zte.batchName,
        Cell: TableCell,
        width: '100px',
        maxWidth: 120,
        minWidth: 100,
      },
      {
        id: Columns.Alias,
        Header: t('alias'),
        accessor: (zte) => zte.alias,
        Cell: TableCellBold,
        width: '100px',
        maxWidth: 100,
        minWidth: 100,
      },
      {
        id: Columns.ModelNumber,
        Header: t('modelNo'),
        accessor: (zte) => zte.modelNumber,
        Cell: TableCell,
        width: '100px',
        maxWidth: 100,
        minWidth: 100,
      },
      {
        id: Columns.SerialNo,
        Header: t('serialNo'),
        accessor: (zte) => zte.serialNumber,
        Cell: TableCell,
        width: '100px',
        maxWidth: 100,
        minWidth: 100,
      },
      {
        id: Columns.MacAddress,
        Header: t('macAddress'),
        accessor: (zte) => zte.macAddress,
        Cell: TableCell,
        width: '120px',
        maxWidth: 120,
        minWidth: 100,
      },
      {
        id: Columns.DateAdded,
        Header: t('DateAdded'),
        accessor: (zte) => zte.createdAt,
        Cell: DateCell,
        width: '100px',
        maxWidth: 100,
        minWidth: 100,
      },
      {
        id: Columns.CustomerName,
        Header: t('customer'),
        accessor: (zte) => zte.customerName,
        Cell: TableCell,
        width: '100px',
        maxWidth: 100,
        minWidth: 100,
      },
      {
        id: Columns.Status,
        Header: t('claimed'),
        accessor: (zte) => zte.status,
        Cell: statusCell,
        width: '80px',
        maxWidth: 80,
        minWidth: 60,
      },
    ],
    [],
  );

  return useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex,
        pageSize,
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
      manualPagination: true,
      pageCount: Math.ceil(totalRowCount / pageSize),
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
  );
}
